import React, { useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as S from './SectionResults.styles'

// Hook para detectar se o elemento está visível na tela
function useOnScreen(ref: React.RefObject<HTMLElement>, rootMargin = '0px') {
  const [isIntersecting, setIntersecting] = useState(false)
  useEffect(() => {
    if (!ref.current) return
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref, rootMargin])
  return isIntersecting
}

// Componente de contador animado
function AnimatedCounter({
  to,
  duration = 1200
}: {
  to: number
  duration?: number
}) {
  const [value, setValue] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const isVisible = useOnScreen(ref as React.RefObject<HTMLElement>)
  useEffect(() => {
    if (!isVisible) return
    const start = 0
    const startTime = performance.now()
    function animate(now: number) {
      const elapsed = now - startTime
      const progress = Math.min(elapsed / duration, 1)
      const current = Math.floor(progress * (to - start) + start)
      setValue(current)
      if (progress < 1) requestAnimationFrame(animate)
      else setValue(to)
    }
    requestAnimationFrame(animate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, to, duration])
  return <span ref={ref}>{value}</span>
}

interface ResultadosCardProps {
  svg: React.ReactNode
  text1: string
  text2?: string
  text3: string
  animatedNumber?: number
  className?: string
}

const ResultadosCard = ({
  svg,
  text1,
  text2,
  text3,
  animatedNumber,
  className
}: ResultadosCardProps) => (
  <S.Card className={className}>
    <S.CardText1>{text1}</S.CardText1>
    <S.CardText2>
      {text2 && <span>{text2} </span>}
      {animatedNumber !== undefined && <AnimatedCounter to={animatedNumber} />}
    </S.CardText2>
    <S.CardText3>{text3}</S.CardText3>
    <S.CardSVG>{svg}</S.CardSVG>
  </S.Card>
)

const svg1 = (
  <img
    src="/assets/defaults/resultados-1.svg"
    alt="Resultados 1"
    width={172}
    height={172}
  />
)
const svg2 = (
  <img
    src="/assets/defaults/resultados-2.svg"
    alt="Resultados 2"
    width={172}
    height={172}
  />
)
const svg3 = (
  <img
    src="/assets/defaults/resultados-3.svg"
    alt="Resultados 3"
    width={172}
    height={172}
  />
)

const SectionResults = () => {
  const { t } = useTranslation()

  return (
    <S.Section>
      <S.Title>{t('landingV2.sectionResults.title')}</S.Title>
      <S.CardsRow>
        <ResultadosCard
          className="alunos"
          svg={svg1}
          text1={t('landingV2.sectionResults.cards.alunos.text1')}
          animatedNumber={70000}
          text3={t('landingV2.sectionResults.cards.alunos.text3')}
        />
        <ResultadosCard
          className="instituicoes"
          svg={svg2}
          text1={t('landingV2.sectionResults.cards.instituicoes.text1')}
          text2={t('landingV2.sectionResults.cards.instituicoes.text2')}
          animatedNumber={100}
          text3={t('landingV2.sectionResults.cards.instituicoes.text3')}
        />
        <ResultadosCard
          className="conteudos"
          svg={svg3}
          text1={t('landingV2.sectionResults.cards.conteudos.text1')}
          animatedNumber={1000}
          text3={t('landingV2.sectionResults.cards.conteudos.text3')}
        />
      </S.CardsRow>
    </S.Section>
  )
}

export default SectionResults
